import { ROLE_CODES } from "@/constants";

export function isProgramAdmin(roleCode?: string): boolean {
  return roleCode === ROLE_CODES.PROGRAM_ADMIN;
}

export function isMerchantAdmin(roleCode?: string): boolean {
  return roleCode === ROLE_CODES.MERCHANT_ADMIN;
}

export function isLoyyalAdmin(roleCode?: string): boolean {
  return roleCode === ROLE_CODES.LOYYAL_ADMIN;
}

export function isPerkPointAdmin(roleCode?: string): boolean {
  return roleCode === ROLE_CODES.PERK_POINT_ADMIN;
}

export function isPerkPointProgramAdmin(roleCode?: string): boolean {
  return roleCode === ROLE_CODES.PERK_POINT_PROGRAM;
}

/** Roles that should not see the Extras sidebar section */
export function shouldHideExtrasSection(roleCode?: string): boolean {
  return isProgramAdmin(roleCode) || isMerchantAdmin(roleCode);
}

/**
 * Jobs / Clients / Users / Settings paths hidden from Extras for restricted roles.
 * Keep in sync with sidebar-config Extras items (accesspoint, xpandpoint, rewardpoint).
 */
const EXTRAS_PATH_PREFIXES = [
  "/jobs",
  "/accesspoint/jobs",
  "/xpandpoint/jobs",
  "/rewardpoint/jobs",
  "/clients",
  "/accesspoint/clients",
  "/xpandpoint/clients",
  "/rewardpoint/clients",
  "/users",
  "/accesspoint/users",
  "/xpandpoint/users",
  "/rewardpoint/users",
  "/settings",
  "/accesspoint/settings",
  "/xpandpoint/settings",
  "/rewardpoint/settings",
] as const;

export function isExtrasPath(pathname: string): boolean {
  return EXTRAS_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

/** Route guard twin of shouldHideExtrasSection — blocks URL access for those roles. */
export function canAccessExtrasPath(
  pathname: string,
  roleCode?: string
): boolean {
  if (!isExtrasPath(pathname)) return true;
  return !shouldHideExtrasSection(roleCode);
}

export function getStoredRoleCode(): string | undefined {
  try {
    const raw =
      sessionStorage.getItem("userInfo") || localStorage.getItem("userInfo");
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return typeof parsed?.roleCode === "string" ? parsed.roleCode : undefined;
  } catch {
    return undefined;
  }
}

/** Hide Accounting Entries in Access Point sidebar */
export function shouldHideAccessPointAccountingEntries(
  roleCode?: string
): boolean {
  return isProgramAdmin(roleCode) || isMerchantAdmin(roleCode);
}

/** Hide Analytics in sidebar */
export function shouldHideAnalytics(roleCode?: string): boolean {
  return isMerchantAdmin(roleCode);
}

/** Merchant admin can manage stores without UPDATE_MERCHANT permission */
export function canManageMerchantStores(
  roleCode: string | undefined,
  hasUpdateMerchantPermission: boolean
): boolean {
  return isMerchantAdmin(roleCode) || hasUpdateMerchantPermission;
}
